import React from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from '@mui/material';
import { SubdirectoryArrowRight } from '@mui/icons-material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useSnackbar } from 'notistack';
import { CategoryTreeConfig, TreeSubCategory } from './types';

const schema = yup.object({
  name: yup.string().trim().required('Name is required').max(100),
  description: yup.string().max(500),
});

/** Add or edit a sub-category. Sub-categories post where their category does. */
const SubCategoryDialog: React.FC<{
  open: boolean;
  categoryId: string;
  editing: TreeSubCategory | null;
  config: CategoryTreeConfig;
  onClose: () => void;
  onSaved: (categoryId: string) => void;
}> = ({ open, categoryId, editing, config, onClose, onSaved }) => {
  const { enqueueSnackbar } = useSnackbar();

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: { name: editing?.name ?? '', description: editing?.description ?? '' },
    validationSchema: schema,
    onSubmit: async values => {
      const input = { name: values.name.trim(), description: values.description };
      try {
        if (editing) await config.service.updateSubCategory(categoryId, editing._id, input);
        else await config.service.createSubCategory(categoryId, input);
        enqueueSnackbar(editing ? 'Sub-category updated' : 'Sub-category created', { variant: 'success' });
        onSaved(categoryId);
        onClose();
      } catch (error: any) {
        enqueueSnackbar(error.message || 'Could not save the sub-category', { variant: 'error' });
      }
    },
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle component="div" sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SubdirectoryArrowRight color={config.accent} />
          <Typography variant="h6" fontWeight={600}>{editing ? 'Edit Sub-Category' : 'Add Sub-Category'}</Typography>
        </Box>
        <Typography variant="caption" color="text.secondary">
          {editing ? 'Update the sub-category details below' : 'Add a sub-category under this category'}
        </Typography>
      </DialogTitle>
      <form onSubmit={formik.handleSubmit}>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 0.5 }}>
            <TextField
              fullWidth
              name="name"
              autoFocus
              label="Sub-Category Name"
              placeholder={config.examples.sub}
              value={formik.values.name}
              onChange={formik.handleChange}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
            />
            <TextField
              fullWidth
              name="description"
              label="Description"
              placeholder="Brief description of this sub-category"
              multiline
              rows={2}
              value={formik.values.description}
              onChange={formik.handleChange}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" color={config.accent} disabled={formik.isSubmitting} sx={{ fontWeight: 600 }}>
            {editing ? 'Save Changes' : 'Create Sub-Category'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default SubCategoryDialog;
